import { useEffect, useState, useRef } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

/**
 * THE UNIXORN ARCHIVE
 * A cinematic editorial universe — mythology, philosophy, and soul of the brand.
 * Sections: Infinity Horn™ · Manifesto · MMXXV Era · Celestial Cream™ Philosophy
 *           · Editorial Gallery · Storytelling · Future Capsules · Closing Quote
 */

const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setShown(true)),
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, shown };
};

const RevealBlock = ({ children, delay = 0, className = "" }) => {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-[1200ms] ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Divider = () => (
  <div className="container-luxury">
    <div className="max-w-md mx-auto py-12 md:py-16 flex items-center justify-center gap-6">
      <span className="h-px w-16 bg-stone-400/50" />
      <span className="w-1.5 h-1.5 rounded-full bg-stone-500/60" />
      <span className="h-px w-16 bg-stone-400/50" />
    </div>
  </div>
);

export default function ChroniclesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4EFE6] relative overflow-x-hidden">
      <Navigation />

      {/* ───────── 1 · HERO ───────── */}
      <section
        className="relative pt-32 md:pt-44 pb-24 md:pb-36 overflow-hidden"
        data-testid="archive-hero"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(255,251,240,1) 0%, rgba(244,239,230,0) 65%)",
          }}
        />
        <div className="container-luxury relative z-10">
          <RevealBlock>
            <div className="text-center max-w-5xl mx-auto">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-8">
                UNIXORN — Cinematic Archive
              </p>
              <h1 className="text-5xl md:text-7xl lg:text-[8.5rem] font-display font-bold text-stone-900 tracking-tight leading-[0.92] mb-10">
                THE UNIXORN<br />ARCHIVE
              </h1>
              <p className="text-lg md:text-2xl text-stone-600 italic max-w-2xl mx-auto leading-relaxed">
                Mythology. Philosophy. The forward step.
              </p>
            </div>
          </RevealBlock>
        </div>
      </section>

      {/* ───────── 2 · INFINITY HORN ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24 items-center max-w-6xl mx-auto">
            <RevealBlock>
              <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.32)]">
                <img
                  src="/assets/images/celestial/sole.png"
                  alt="The Infinity Horn — UNIXORN's eternal emblem"
                  className="w-full h-full object-contain p-4"
                />
                <div className="absolute bottom-5 left-5 px-3.5 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                    The Emblem
                  </span>
                </div>
              </div>
            </RevealBlock>

            <RevealBlock delay={200}>
              <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
                Chapter One
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight mb-9 leading-[1.05]">
                THE INFINITY<br />HORN<sup className="text-xl md:text-2xl align-top">™</sup>
              </h2>
              <div className="space-y-5 text-stone-700 text-base md:text-lg leading-relaxed">
                <p>
                  A single closed line. No beginning. No end. The Infinity Horn
                  is the foundational mark of UNIXORN — the silhouette of a
                  unicorn distilled into one continuous gesture.
                </p>
                <p>
                  It is worn quietly. Embossed at the heel. Sewn into the lining.
                  Whispered into the form. A signal for those who recognise it.
                </p>
                <p className="text-stone-500 italic pt-3">
                  Not a logo. A signature.
                </p>
              </div>
            </RevealBlock>
          </div>
        </div>
      </section>

      <Divider />

      {/* ───────── 3 · MANIFESTO ───────── */}
      <section className="relative py-24 md:py-40 bg-[#F4EFE6]">
        <div className="container-luxury">
          <RevealBlock>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-10">
                The Manifesto
              </p>
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-stone-900 tracking-tight leading-[1.02] mb-12">
                UNIQUE &amp; ORIGINAL.
              </h2>
              <div className="space-y-6 text-stone-700 text-lg md:text-xl leading-relaxed">
                <p>
                  UNIXORN exists at the crossing of <em>form</em> and <em>meaning</em>.
                </p>
                <p>
                  Born from the idea that rarity is earned — not manufactured —
                  the brand explores identity through objects designed to endure.
                </p>
                <p>
                  Every piece intentional.<br />
                  Every silhouette considered.<br />
                  Nothing accidental.
                </p>
                <p className="text-stone-500 italic pt-6">
                  We are not trend-driven.<br />
                  We are artifact-driven.
                </p>
              </div>
            </div>
          </RevealBlock>
        </div>
      </section>

      <Divider />

      {/* ───────── 4 · MMXXV ERA ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-24 items-center max-w-6xl mx-auto">
            <RevealBlock>
              <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
                The Era
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight mb-9 leading-[1.05]">
                MMXXV.<br />THE QUIET ERA.
              </h2>
              <div className="space-y-5 text-stone-700 text-base md:text-lg leading-relaxed">
                <p>
                  The current chapter is restrained. Monochrome. Embossed.
                  The volume is turned down, the form is turned up.
                </p>
                <p>
                  Pieces from the MMXXV era carry the date sewn into the language —
                  a quiet stamp of when they crossed the portal.
                </p>
                <p className="text-stone-500 italic pt-3">
                  No noise. Only signal.
                </p>
              </div>
            </RevealBlock>

            <RevealBlock delay={200}>
              <div className="relative aspect-[4/5] overflow-hidden bg-[#F5F0E5] shadow-[0_40px_100px_-40px_rgba(120,100,72,0.32)]">
                <img
                  src="/assets/images/cream/cream-set.png"
                  alt="MMXXV — The Cream Era"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-5 left-5 px-3.5 py-2 bg-[#F4EFE6]/85 backdrop-blur-sm border border-stone-400/40">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-stone-700 font-semibold">
                    MMXXV · Cream Era
                  </span>
                </div>
              </div>
            </RevealBlock>
          </div>
        </div>
      </section>

      {/* ───────── 5 · CELESTIAL CREAM PHILOSOPHY ───────── */}
      <section className="relative py-24 md:py-40 bg-[#F4EFE6]">
        <div className="container-luxury">
          <RevealBlock>
            <div className="max-w-4xl mx-auto text-center mb-20">
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-8">
                The Philosophy
              </p>
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-stone-900 tracking-tight leading-[1.02] mb-10">
                CELESTIAL CREAM<sup className="text-xl md:text-2xl lg:text-3xl align-top">™</sup>
              </h2>
              <p className="text-stone-700 text-lg md:text-xl leading-relaxed italic max-w-2xl mx-auto">
                A palette is not a colour. It is a posture toward the world.
              </p>
            </div>
          </RevealBlock>

          <RevealBlock delay={150}>
            <div className="max-w-5xl mx-auto">
              <div className="relative aspect-[16/10] overflow-hidden bg-[#F5F0E5] shadow-[0_60px_140px_-40px_rgba(120,100,72,0.32)]">
                <img
                  src="/assets/images/celestial/main.png"
                  alt="Celestial Cream Low — the philosophy in form"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </RevealBlock>

          <RevealBlock delay={300}>
            <div className="max-w-3xl mx-auto mt-16 text-center space-y-5 text-stone-700 text-base md:text-lg leading-relaxed">
              <p>
                Cream is not absence of colour. It is the colour of pause —
                of the moment before, of skin in early light, of breath held
                between intention and action.
              </p>
              <p>
                Celestial Cream™ is the UNIXORN posture: quiet but immovable,
                soft but rare, monochrome but never plain.
              </p>
            </div>
          </RevealBlock>
        </div>
      </section>

      <Divider />

      {/* ───────── 6 · EDITORIAL GALLERY ───────── */}
      <section className="relative py-24 md:py-36 bg-[#EBE3D2] border-y border-stone-300/50">
        <div className="container-luxury">
          <RevealBlock>
            <div className="text-center mb-16 md:mb-20">
              <p className="text-[11px] tracking-[0.45em] uppercase text-stone-500 mb-6">
                Editorial Gallery
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-stone-900 tracking-tight leading-[1.05]">
                FRAMES FROM<br />THE ARCHIVE
              </h2>
            </div>
          </RevealBlock>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-7 max-w-6xl mx-auto">
            {[
              { src: "/assets/images/cream/saint-tee.png", label: "Saint of Originality" },
              { src: "/assets/images/celestial/side.png", label: "The Silhouette" },
              { src: "/assets/images/cream/sigil-tee.png", label: "The Sigil" },
              { src: "/assets/images/cream/cream-hoodie.png", label: "The Foundation" },
              { src: "/assets/images/celestial/back.png", label: "The Heel" },
              { src: "/assets/images/cream/bucket-hat.png", label: "The Crown" },
            ].map((item, idx) => (
              <RevealBlock key={item.src} delay={idx * 80}>
                <div className="relative aspect-square overflow-hidden bg-[#F5F0E5] group cursor-pointer">
                  <img
                    src={item.src}
                    alt={item.label}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 bg-gradient-to-t from-[#F4EFE6]/90 via-[#F4EFE6]/40 to-transparent">
                    <p className="text-stone-800 text-[10px] md:text-xs tracking-[0.3em] uppercase font-semibold">
                      {item.label}
                    </p>
                  </div>
                </div>
              </RevealBlock>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── 7 · CINEMATIC STORYTELLING ───────── */}
      <section className="relative py-24 md:py-40 bg-[#F4EFE6]">
        <div className="container-luxury max-w-3xl">
          <RevealBlock>
            <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500 mb-8 text-center">
              The Story
            </p>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-stone-900 tracking-tight mb-14 text-center leading-[1.05]">
              A FIGURE WALKS<br />THROUGH THE PORTAL.
            </h2>
            <div className="space-y-7 text-stone-700 text-lg md:text-xl leading-relaxed">
              <p>
                The portal does not announce itself. It opens only when stepped
                toward. Some never see it. Some pretend not to.
              </p>
              <p>
                A figure crosses. Wears cream. Carries the Horn. Walks forward
                without looking back at the version of themselves left at the
                threshold.
              </p>
              <p>
                Each UNIXORN piece is a relic from this crossing — proof that
                someone moved when others stood still.
              </p>
              <p className="text-stone-500 italic pt-5 text-center">
                The artifact remembers what the world tries to forget.
              </p>
            </div>
          </RevealBlock>
        </div>
      </section>

      <Divider />

      {/* ───────── 9 · CLOSING QUOTE ───────── */}
      <section className="relative py-32 md:py-44 bg-[#F4EFE6]">
        <div className="container-luxury">
          <RevealBlock>
            <div className="max-w-3xl mx-auto text-center">
              <span className="block text-stone-400 text-5xl md:text-7xl font-display leading-none mb-8 select-none">
                &ldquo;
              </span>
              <p className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-stone-900 leading-[1.15] tracking-tight mb-10">
                Each UNIXORN piece<br />exists once —<br />then becomes legend.
              </p>
              <p className="text-[11px] tracking-[0.5em] uppercase text-stone-500">
                UNIXORN — MMXXV · End of Archive
              </p>
            </div>
          </RevealBlock>
        </div>
      </section>

      <Footer />
    </div>
  );
}
